import './TicketEdit.scss';

import { CheckCircleOutlined, CloseCircleOutlined, RollbackOutlined } from '@ant-design/icons';
import { Edit, RefreshButton, SaveButton, useForm } from '@refinedev/antd';
import { useGo, useNotification } from '@refinedev/core';
import { Button, Flex, Tabs, Tag, Tooltip } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import ConfirmModal from '@/components/general/ConfirmModal';
import ProtectedDeleteButton from '@/components/protected/ProtectedDeleteButton';
import TicketLogisticStateTag from '@/components/ticket/TicketLogisticStateTag';
import TicketStateTag from '@/components/ticket/TicketStateTag';
import FileData from '@/model/General/FileData';
import { Ticket } from '@/model/Ticket/Ticket';
import { TICKET_LOGISTIC_STATE, TICKET_STATE } from '@/model/Ticket/TicketConstants';
import { useRepositoriesContext } from '@/repositories/RepositoriesContext';
import { RESOURCES } from '@/Resources';

import TicketAITimeline from './TicketAITimeline/TicketAITimeline';
import TicketForm from './TicketForm/TicketForm';
import TicketShowLogistic from './TicketShow/TicketShowLogistic';
import TicketShowServiceOrders from './TicketShow/TicketShowServiceOrders';

export const TicketEdit = () => {
  const { t } = useTranslation();
  const go = useGo();
  const { open } = useNotification();
  const { ticketRepository } = useRepositoriesContext();
  const pendingSubmitStatusRef = useRef<TICKET_STATE>();
  const statusChangeAfterSaveRef = useRef(false);
  const continueEditingRef = useRef(false);
  const { formProps, saveButtonProps, query } = useForm<Ticket>({
    redirect: false,
    successNotification: () => {
      return {
        message: ``,
        description: t('El ticket fue actualizado correctamente'),
        type: 'success',
      };
    },
    errorNotification: () => {
      return {
        message: '',
        description: t('Ocurrió un error al actualizar el ticket'),
        type: 'error',
      };
    },
    onMutationSuccess: response => {
      const mutationSuccess = async () => {
        const id = response?.data?.id;

        // Si hay archivos, los subimos en serie y notificamos errores de forma clara.
        if (id && fileList.length > 0) {
          try {
            await ticketRepository.filesManagement(fileList, id);
          } catch {
            open?.({
              message: '',
              description: t('Ocurrió un error al subir los archivos al ticket'),
              type: 'error',
            });
            // No navegamos si falló la subida: dejamos al usuario en la pantalla de edición.
            pendingSubmitStatusRef.current = undefined;
            statusChangeAfterSaveRef.current = false;
            continueEditingRef.current = false;
            setBlockLogisticTab(false);

            return;
          }
        }

        if (statusChangeAfterSaveRef.current) {
          void query?.refetch();
          setSelectedTab('1');
        }

        // Navegación condicional luego de guardar/subir archivos.
        if (id && !continueEditingRef.current) {
          go({ to: { resource: RESOURCES.TICKETS, action: 'list' }, type: 'replace' });
        }

        pendingSubmitStatusRef.current = undefined;
        statusChangeAfterSaveRef.current = false;
        continueEditingRef.current = false;
        setBlockLogisticTab(false);
      };

      void mutationSuccess();
    },
  });
  const [fileList, setFileList] = useState<FileData[]>([]);

  useEffect(() => {
    if (formProps.initialValues?.files) {
      const fileList = formProps.initialValues.files.map(fileData => {
        return new FileData(fileData);
      });

      setFileList(fileList);
    }
  }, [formProps.initialValues?.files]);

  useEffect(() => {
    const notModified = fileList.every(
      f =>
        formProps.initialValues?.files?.some(initial_file => initial_file.id === f.id) &&
        !f.removed,
    );

    if (!notModified) {
      setBlockLogisticTab(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileList]);

  const [selectedTab, setSelectedTab] = useState<string>('1');
  const [blockLogisticTab, setBlockLogisticTab] = useState(false);
  const submitTicketWithStatus = (status: TICKET_STATE) => {
    pendingSubmitStatusRef.current = status;
    statusChangeAfterSaveRef.current = true;
    continueEditingRef.current = true;
    formProps.form?.submit();
  };

  const clearPendingStatusSubmit = () => {
    pendingSubmitStatusRef.current = undefined;
    statusChangeAfterSaveRef.current = false;
    continueEditingRef.current = false;
    setSelectedTab('1');
  };

  // Replica la validación del formulario para impedir cierre/resolución con logística pendiente.
  const canFinalizeTicket = Boolean(
    (formProps.initialValues as Ticket | undefined)?.logistics_can_close,
  );
  const canFinalizeMessage = canFinalizeTicket
    ? ''
    : t('No se puede cerrar o resolver el ticket, revise la logística pendiente');

  return (
    <Edit
      isLoading={query?.isLoading || query?.isFetching}
      footerButtons={({ deleteButtonProps }) =>
        selectedTab == '1' && (
          <>
            <ProtectedDeleteButton
              {...deleteButtonProps}
              disabled={query?.data?.data.has_receptions || query?.data?.data.has_shipments}
              tooltip={
                query?.data?.data.has_receptions || query?.data?.data.has_shipments
                  ? t('No se puede eliminar el ticket porque tiene logística asociada')
                  : undefined
              }
            />

            <SaveButton {...saveButtonProps} />
            <SaveButton
              {...saveButtonProps}
              onClick={() => {
                continueEditingRef.current = true;
                saveButtonProps.onClick();
              }}>
              {t('Guardar y continuar editando')}
            </SaveButton>
          </>
        )
      }
      headerButtons={({ refreshButtonProps }) => (
        <>
          <RefreshButton {...refreshButtonProps} key="refresh" hideText />

          {(formProps.initialValues as Ticket)?.status === TICKET_STATE.OPEN ? (
            <>
              <ConfirmModal
                key="CloseTicket"
                openButton={
                  <Tooltip title={canFinalizeMessage}>
                    <Button disabled={!canFinalizeTicket} icon={<CloseCircleOutlined />}>
                      {t('Cerrar')}
                    </Button>
                  </Tooltip>
                }
                onOk={() => {
                  submitTicketWithStatus(TICKET_STATE.CLOSED);
                }}
                title={t('¿Cerrar el ticket sin resolver?')}
                message={
                  <>
                    {t('El ticket pasará a cerrado sin quedar marcado como resuelto')}.{' '}
                    {t('Se guardarán los cambios realizados')}
                  </>
                }
              />
              <ConfirmModal
                key="ResolveTicket"
                openButton={
                  <Tooltip title={canFinalizeMessage}>
                    <Button
                      disabled={!canFinalizeTicket}
                      type="primary"
                      icon={<CheckCircleOutlined />}>
                      {t('Resolver')}
                    </Button>
                  </Tooltip>
                }
                onOk={() => {
                  submitTicketWithStatus(TICKET_STATE.RESOLVED);
                }}
                title={t('¿Marcar el ticket como resuelto?')}
                message={
                  <>
                    {t('El ticket quedará resuelto y se dará por finalizada la gestión')}.{' '}
                    {t('Se guardarán los cambios realizados')}
                  </>
                }
              />
            </>
          ) : (
            <ConfirmModal
              key="ReopenTicket"
              openButton={<Button icon={<RollbackOutlined />}>{t('Reabrir')}</Button>}
              onOk={() => {
                submitTicketWithStatus(TICKET_STATE.OPEN);
              }}
              title={t('¿Reabrir el ticket?')}
              message={
                <>
                  {t('El ticket volverá a quedar abierto para continuar su seguimiento')}.{' '}
                  {t('Se guardarán los cambios realizados')}
                </>
              }
            />
          )}
        </>
      )}>
      {query?.data && (
        <>
          {formProps.initialValues && (
            <TicketEditHeader ticket={formProps.initialValues as Ticket} />
          )}
          <Tabs
            defaultActiveKey="1"
            activeKey={selectedTab}
            onChange={key => setSelectedTab(key)}
            items={[
              {
                label: t('Detalles'),
                key: '1',
                children: (
                  <TicketForm
                    formProps={formProps}
                    fileList={fileList}
                    setFileList={setFileList}
                    getSubmitStatus={() => pendingSubmitStatusRef.current}
                    onSubmitFailed={clearPendingStatusSubmit}
                    setBlockLogisticTab={setBlockLogisticTab}
                  />
                ),
              },
              {
                disabled: blockLogisticTab,
                label: (
                  <Tooltip
                    title={
                      blockLogisticTab ? t('Guarde los cambios para acceder a la logística') : ''
                    }>
                    {t('Logística')}
                  </Tooltip>
                ),
                key: '2',
                children: <TicketShowLogistic record={formProps.initialValues as Ticket} />,
              },
              {
                disabled: blockLogisticTab,
                label: (
                  <Tooltip
                    title={
                      blockLogisticTab
                        ? t('Guarde los cambios para acceder a las órdenes de servicio')
                        : ''
                    }>
                    {t('Órdenes de servicio')}
                  </Tooltip>
                ),
                key: '3',
                children: <TicketShowServiceOrders record={formProps.initialValues as Ticket} />,
              }, // Agregamos la tab extra solo si el el ticket tiene Timeline.
              ...(formProps.initialValues?.ia_timeline &&
              formProps.initialValues?.ia_timeline.length > 0
                ? [
                    {
                      disabled: blockLogisticTab,
                      label: t('Resumen de IA'),
                      key: '4',
                      children: <TicketAITimeline record={formProps.initialValues as Ticket} />,
                    },
                  ]
                : []),
            ]}
          />
        </>
      )}
    </Edit>
  );
};

interface TicketEditHeaderProps {
  ticket: Ticket;
}

const TicketEditHeader = (props: TicketEditHeaderProps) => {
  const { t } = useTranslation();
  const { ticket } = props;

  return (
    <div className="TicketEditHeader">
      <div>
        <div className="TicketEditHeaderId">
          <b>Ticket id:</b> {ticket.id}
        </div>
        <Flex align="center" gap={8} style={{ marginTop: 6 }} wrap>
          <b>{t('Estado')}: </b>
          <TicketStateTag ticketState={ticket.status} />
          {ticket.on_hold && <Tag color="default">{t('En pausa')}</Tag>}
          {ticket.no_tracking && <Tag color="red">{t('Sin seguimiento')}</Tag>}
        </Flex>
        {ticket.logistics_state != undefined &&
          ticket.logistics_state !== TICKET_LOGISTIC_STATE.SIN_GESTION_LOGISTICA && (
            <Flex align="center" gap={8} style={{ marginTop: 6 }}>
              <b>{t('Estado de logística')}: </b>
              <TicketLogisticStateTag ticketLogisticState={ticket.logistics_state} />
            </Flex>
          )}
      </div>

      <div>
        {ticket.created && typeof ticket.created == 'string' && (
          <div>
            <b>{t('Fecha creación')}:</b> {dayjs(ticket.created).format('L HH:mm')}
          </div>
        )}
        {ticket.modified && typeof ticket.modified == 'string' && (
          <div>
            <b>{t('Fecha última modificación')}:</b> {dayjs(ticket.modified).format('L HH:mm')}
          </div>
        )}
      </div>
    </div>
  );
};
